import { Notification } from '@/graphql/appsync-notifications'

import { NotificationDataIProps } from './types'

export const isUniqueNotification = (
  notifications: Notification[],
  data: NotificationDataIProps
) =>
  !notifications.some(
    (n) => n.notificationTimestamp === data?.notification?.notificationTimestamp
  )

// A GSOC screen stays open for days, so what is remembered is capped.
const MAX_HANDLED_NOTIFICATIONS = 500

/**
 * Claims a notification for processing: true the first time its timestamp is
 * seen, false for a re-delivery of one already claimed.
 *
 * `isUniqueNotification` can't do this alone. The Redux list it reads only
 * holds notifications that were *shown*, so a muted or filtered-out one is
 * never recorded there, and a re-delivery would be handled again. That used to
 * be harmless; now a muted Critical incident plays its sound under the
 * organization policy, and a duplicate would sound the alarm twice.
 *
 * A notification without a timestamp can't be told apart, so it is never
 * suppressed.
 */
export const claimNotification = (
  handledTimestamps: Set<string>,
  data?: NotificationDataIProps
): boolean => {
  const timestamp = data?.notification?.notificationTimestamp
  if (timestamp === undefined || timestamp === null) return true

  const key = String(timestamp)
  if (handledTimestamps.has(key)) return false

  handledTimestamps.add(key)
  if (handledTimestamps.size > MAX_HANDLED_NOTIFICATIONS) {
    // A Set iterates in insertion order, so the first value is the oldest.
    const oldest = handledTimestamps.values().next().value
    if (oldest !== undefined) handledTimestamps.delete(oldest)
  }
  return true
}
